import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormSelect } from '../../ui/Select'

const VariableFiltersSection = ({
  showSection,
  toggleSection,
  activeFilters,
  variableOptions,
  valueOptions,
  collect,
  fieldErrors,
  onAddFilter,
  onRemoveFilter,
  onVariableChange,
  onValuesChange
}) => {
  const handleVariableChange = useCallback((uniqueId, selected) => {
    onVariableChange(uniqueId, selected?.value)
  }, [onVariableChange])

  const handleValuesChange = useCallback((uniqueId, selected) => {
    onValuesChange(uniqueId, selected?.map(option => option.value) || [])
  }, [onValuesChange])

  const renderNoOptionsMessage = useCallback(variableId => {
    if (variableId)
      return 'Aucune option disponible'

    return 'Veuillez d\'abord sélectionner une variable'
  }, [])

  const handleVariableChangeForFilter = useCallback(uniqueId =>
    selected => handleVariableChange(uniqueId, selected), [handleVariableChange])

  const handleValuesChangeForFilter = useCallback(uniqueId =>
    selected => handleValuesChange(uniqueId, selected), [handleValuesChange])

  const handleRemoveFilter = useCallback(uniqueId =>
    () => onRemoveFilter(uniqueId), [onRemoveFilter])

  const handleNoOptionsMessage = useCallback(variableId =>
    () => renderNoOptionsMessage(variableId), [renderNoOptionsMessage])

  return (
    <div className="pt-3">
      <div
        className="bg-white d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={toggleSection}
      >
        <h6 className="m-0 font-weight-bolder">
          Filtrer par variables
          (
          {activeFilters.length}
          )
        </h6>
        <i className={`fas fa-chevron-${showSection ? 'up' : 'down'}`} />
      </div>
      {showSection ? (
        <div className="py-3">
          {activeFilters.map(filter => (
            <div key={filter.uniqueId} className="bg-light mb-3">
              <div className="d-flex align-items-center p-3">
                <div className="flex-grow-1">
                  <FormSelect
                    className="mb-2"
                    options={variableOptions}
                    value={filter.variableId ? {
                      value: filter.variableId,
                      label: collect.variables?.find(variable =>
                        variable.id.toString() === filter.variableId.toString()
                      )?.name
                    } : null}
                    placeholder="Sélectionner une variable"
                    errorMessage={fieldErrors[`variable_${filter.uniqueId}`] || ''}
                    isClearable
                    onChange={handleVariableChangeForFilter(filter.uniqueId)}
                  />
                  <FormSelect
                    closeMenuOnSelect={false}
                    errorMessage={fieldErrors[`variable_values_${filter.uniqueId}`] || ''}
                    isDisabled={!filter.variableId}
                    noOptionsMessage={handleNoOptionsMessage(filter.variableId)}
                    options={valueOptions(filter.variableId)}
                    placeholder="Sélectionner des valeurs"
                    value={filter.values?.map(value => {
                      const options = valueOptions(filter.variableId)
                      const option = options.find(opt => opt.value === value)
                      return option || { value, label: value }
                    })}
                    isClearable
                    isMulti
                    showSelectAll
                    onChange={handleValuesChangeForFilter(filter.uniqueId)}
                  />
                </div>
                <button
                  type="button"
                  className="btn p-0 pl-3 hover-danger"
                  onClick={handleRemoveFilter(filter.uniqueId)}
                >
                  <i className="fas fa-trash" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-link text-dark p-0 hover-bold"
            style={{ textDecoration: 'none' }}
            onClick={onAddFilter}
          >
            <i className="fas fa-plus mr-1" />
            {' '}
            Ajouter une variable
          </button>
        </div>
      ) : null}
    </div>
  )
}

VariableFiltersSection.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.shape({
    variableId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    values: PropTypes.arrayOf(PropTypes.string),
    uniqueId: PropTypes.string
  })).isRequired,
  collect: PropTypes.shape({
    variables: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }))
  }).isRequired,
  fieldErrors: PropTypes.objectOf(PropTypes.string).isRequired,
  onAddFilter: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  onVariableChange: PropTypes.func.isRequired,
  showSection: PropTypes.bool.isRequired,
  toggleSection: PropTypes.func.isRequired,
  valueOptions: PropTypes.func.isRequired,
  variableOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string
  })).isRequired
}

export default VariableFiltersSection